/**
 * Paper Selector panel component
 */

import { useState } from 'react';
import {
  Slider,
  CollapsibleCard,
  Switch,
  cn,
} from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';
import { useIsMobile } from '../../hooks/useIsMobile';

/** Paper type presets */
const PAPER_PRESETS = [
  {
    id: 'paper-smooth',
    name: 'Smooth',
    type: 'smooth',
    color: '#ffffff',
    texture: 'none',
    description: 'Clean, smooth finish'
  },
  {
    id: 'paper-matte',
    name: 'Matte',
    type: 'matte',
    color: '#fafafa',
    texture: 'subtle',
    description: 'Non-reflective surface'
  },
  {
    id: 'paper-canvas',
    name: 'Canvas',
    type: 'canvas',
    color: '#f5f0e6',
    texture: 'woven',
    description: 'Woven canvas texture'
  },
  {
    id: 'paper-watercolor',
    name: 'Watercolor',
    type: 'watercolor',
    color: '#fffef8',
    texture: 'rough',
    description: 'Cold-pressed texture'
  },
  {
    id: 'paper-rice',
    name: 'Rice (Xuan)',
    type: 'rice',
    color: '#f8f5e8',
    texture: 'fiber',
    description: 'Traditional Asian paper'
  },
  {
    id: 'paper-mulberry',
    name: 'Mulberry',
    type: 'mulberry',
    color: '#f5f0e0',
    texture: 'fiber',
    description: 'Handmade Korean paper'
  },
  {
    id: 'paper-linen',
    name: 'Linen',
    type: 'linen',
    color: '#f0ebe0',
    texture: 'woven',
    description: 'Fine linen texture'
  },
  {
    id: 'paper-antique',
    name: 'Antique',
    type: 'antique',
    color: '#e8dcc8',
    texture: 'aged',
    description: 'Aged, vintage look'
  },
  {
    id: 'paper-vellum',
    name: 'Vellum',
    type: 'vellum',
    color: '#f5efe5',
    texture: 'smooth',
    description: 'Semi-translucent finish'
  },
  {
    id: 'paper-handmade',
    name: 'Handmade',
    type: 'handmade',
    color: '#f0e8d8',
    texture: 'irregular',
    description: 'Organic, deckled edges'
  },
];

/** Create paper texture thumbnail SVG */
function createPaperThumbnail(color: string, texture: string): string {
  let texturePattern = '';

  switch (texture) {
    case 'woven':
      texturePattern = `
        <pattern id="woven" width="4" height="4" patternUnits="userSpaceOnUse">
          <path d="M0 2h4M2 0v4" stroke="rgba(0,0,0,0.08)" stroke-width="0.5"/>
        </pattern>
        <rect fill="url(#woven)" width="80" height="80"/>
      `;
      break;
    case 'rough':
      texturePattern = `
        <filter id="rough">
          <feTurbulence baseFrequency="0.04" numOctaves="3" result="noise"/>
          <feDiffuseLighting in="noise" lighting-color="white" surfaceScale="1.5">
            <feDistantLight azimuth="45" elevation="60"/>
          </feDiffuseLighting>
        </filter>
        <rect fill="white" filter="url(#rough)" width="80" height="80" opacity="0.3"/>
      `;
      break;
    case 'fiber':
      texturePattern = `
        <filter id="fiber">
          <feTurbulence baseFrequency="0.02 0.1" numOctaves="2" result="noise"/>
          <feColorMatrix type="saturate" values="0"/>
        </filter>
        <rect fill="rgba(139,119,101,0.1)" filter="url(#fiber)" width="80" height="80"/>
      `;
      break;
    case 'aged':
      texturePattern = `
        <filter id="aged">
          <feTurbulence baseFrequency="0.05" numOctaves="2" result="noise"/>
          <feColorMatrix type="matrix" values="1 0 0 0 0.05  0 1 0 0 0.02  0 0 1 0 -0.05  0 0 0 1 0"/>
        </filter>
        <rect fill="rgba(139,90,43,0.15)" filter="url(#aged)" width="80" height="80"/>
      `;
      break;
    case 'subtle':
      texturePattern = `
        <filter id="subtle">
          <feTurbulence baseFrequency="0.8" numOctaves="4" result="noise"/>
          <feColorMatrix type="saturate" values="0"/>
        </filter>
        <rect fill="rgba(0,0,0,0.03)" filter="url(#subtle)" width="80" height="80"/>
      `;
      break;
    case 'irregular':
      texturePattern = `
        <filter id="irreg">
          <feTurbulence baseFrequency="0.03" numOctaves="3" result="noise"/>
        </filter>
        <rect fill="rgba(101,67,33,0.1)" filter="url(#irreg)" width="80" height="80"/>
      `;
      break;
    default:
      texturePattern = '';
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
    <rect fill="${color}" width="80" height="80"/>
    ${texturePattern}
    <rect fill="none" width="80" height="80" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
  </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export interface PaperSelectorProps {
  /** Custom class name */
  className?: string;
}

/** Paper selector component */
export function PaperSelector({ className }: PaperSelectorProps) {
  const selectedArtwork = useSelectedArtwork();
  const updatePaper = useProjectStore((s) => s.updatePaper);
  const updateDisplayScale = useProjectStore((s) => s.updateDisplayScale);

  const [selectedPreset, setSelectedPreset] = useState<string>(
    selectedArtwork?.paper?.templateId || 'paper-smooth'
  );
  const isMobile = useIsMobile();

  // Check if paper is enabled
  const paperEnabled = !!selectedArtwork?.paper;

  // Handle paper toggle
  const handleToggle = (enabled: boolean) => {
    if (!selectedArtwork) return;

    if (enabled) {
      // Enable paper with default settings
      updatePaper(selectedArtwork.id, {
        templateId: selectedPreset,
        textureIntensity: 0.5,
        displacementIntensity: 0.3,
      });
    } else {
      // Disable paper
      updatePaper(selectedArtwork.id, null);
    }
  };

  // Handle preset selection
  const handlePresetSelect = (presetId: string) => {
    if (!selectedArtwork) return;
    setSelectedPreset(presetId);

    if (paperEnabled) {
      updatePaper(selectedArtwork.id, { templateId: presetId });
    }
  };

  // Handle texture intensity change
  const handleTextureIntensity = (intensity: number) => {
    if (!selectedArtwork || !paperEnabled) return;
    updatePaper(selectedArtwork.id, { textureIntensity: intensity });
  };

  // Handle displacement intensity change
  const handleDisplacementIntensity = (intensity: number) => {
    if (!selectedArtwork || !paperEnabled) return;
    updatePaper(selectedArtwork.id, { displacementIntensity: intensity });
  };

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-muted-foreground', className)}>
        Select an artwork to customize its paper
      </div>
    );
  }

  const currentPreset = PAPER_PRESETS.find((p) => p.id === selectedPreset);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Paper toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Paper Effect</h3>
            <p className="text-xs text-muted-foreground">
              Add texture and material to your artwork
            </p>
          </div>
          <Switch checked={paperEnabled} onCheckedChange={handleToggle} />
        </div>
      </div>

      {/* Paper options */}
      <div className={cn('flex-1 overflow-auto p-4', !paperEnabled && 'opacity-50 pointer-events-none')}>
        <h4 className="text-sm font-medium mb-3">Paper Type</h4>

        {/* Paper type grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {PAPER_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetSelect(preset.id)}
              className={cn(
                'relative rounded-lg border-2 transition-all overflow-hidden',
                'hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary',
                selectedPreset === preset.id
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-transparent hover:border-muted-foreground/30'
              )}
            >
              <img
                src={createPaperThumbnail(preset.color, preset.texture)}
                alt={preset.name}
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                <span className="text-xs font-medium text-white">{preset.name}</span>
              </div>
              {selectedPreset === preset.id && (
                <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Paper description */}
        {currentPreset && (
          <p className="text-xs text-center text-muted-foreground mb-4">
            {currentPreset.description}
          </p>
        )}
      </div>

      {/* Paper controls */}
      <CollapsibleCard
        title="Paper Settings"
        collapsible={isMobile}
        className="m-4 mt-0"
        disabled={!paperEnabled}
      >
        {/* Artwork size */}
        <div>
          <label className="text-xs text-muted-foreground">Artwork Size</label>
          <Slider
            value={selectedArtwork.displayScale ?? 1}
            min={0.5}
            max={2}
            step={0.05}
            onChange={(value) => updateDisplayScale(selectedArtwork.id, value)}
            showValue
            formatValue={(v) => `${Math.round(v * 100)}%`}
          />
        </div>

        {/* Texture intensity */}
        <div>
          <label className="text-xs text-muted-foreground">Texture Intensity</label>
          <Slider
            value={selectedArtwork.paper?.textureIntensity ?? 0.5}
            min={0}
            max={1}
            step={0.05}
            onChange={handleTextureIntensity}
            showValue
            formatValue={(v) => `${Math.round(v * 100)}%`}
          />
        </div>

        {/* Displacement intensity (3D effect) */}
        <div>
          <label className="text-xs text-muted-foreground">3D Depth Effect</label>
          <Slider
            value={selectedArtwork.paper?.displacementIntensity ?? 0.3}
            min={0}
            max={1}
            step={0.05}
            onChange={handleDisplacementIntensity}
            showValue
            formatValue={(v) => `${Math.round(v * 100)}%`}
          />
        </div>
      </CollapsibleCard>
    </div>
  );
}
